import { useState } from "react";
import { useEffect } from "react";
import { connect } from "react-redux";

import DeleteIcon from "@mui/icons-material/Delete";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import { Grid, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import { v4 as uuid } from "uuid";

import { deepCopyObject, isValidEmail } from "../../utils/helpers";
import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogTab from "../ui/omniDialogTab";
import DialogTabs from "../ui/omniDialogTabs";
import DialogTitle from "../ui/omniDialogTitle";
import OmniPhoneNumberField from "../ui/omniPhoneNumberField";
import OmniResponsiveButton from "../ui/omniResponsiveButton";
import OmniTextField from "../ui/omniTextField";
import AlertDialog from "./alertDialog";

const PracticeDialog = props => {
  const {
    open,
    dialogTitle = "Add Practice",
    okMessage = "OK",
    cancelMessage = "Cancel",
    deleteMessage = "Delete",
    maxWidth = "sm",
    fullWidth = true,
    omniDMO, //omni Data Model Object, a root level object representation, Pass in a COPY, the only way this works
  } = props;

  const theme = useTheme();

  const [editedDMO, setEditedDMO] = useState(omniDMO);
  const [editedAddress, setEditedAddress] = useState(omniDMO.omniData.address);

  // Required Fields and Validation
  const [readyOk, setReadyOk] = useState(false);
  const [practiceNameValid, setPracticeNameValid] = useState(false);
  const [practicePhoneValid, setPracticePhoneValid] = useState(false);
  const [practiceEmailValid, setPracticeEmailValid] = useState(false);

  const udpatePracticeDMO = event => {
    try {
      editedDMO.omniData[event.target.id] = event.target.value;
      setEditedDMO(deepCopyObject(editedDMO));
    } catch (error) {
      console.log(error);
    }
  };

  const updateAddressDMO = event => {
    try {
      editedAddress[event.target.id] = event.target.value;

      setEditedAddress(deepCopyObject(editedAddress));
    } catch (error) {
      console.log(error);
    }
  };

  const [tabIndex, setTabIndex] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);

  // Check Practice name
  useEffect(() => {
    editedDMO.omniData.name ? setPracticeNameValid(true) : setPracticeNameValid(false);
  }, [editedDMO.omniData.name]);

  // Check practice phone format
  useEffect(() => {
    editedDMO.omniData.phone ? setPracticePhoneValid(true) : setPracticePhoneValid(false);
  }, [editedDMO.omniData.phone]);

  // Check practice email
  useEffect(() => {
    editedDMO.omniData.email && isValidEmail(editedDMO.omniData.email)
      ? setPracticeEmailValid(true)
      : setPracticeEmailValid(false);
  }, [editedDMO.omniData.email]);

  useEffect(() => {
    practiceNameValid && practicePhoneValid && practiceEmailValid
      ? setReadyOk(true)
      : setReadyOk(false);
  }, [practiceNameValid, practicePhoneValid, practiceEmailValid]);

  const onAcceptHandler = () => {
    //notice we didn't touch the id
    editedDMO.omniData.address = editedAddress;

    props.onAccept(editedDMO);
  };

  const onCloseHandler = () => {
    props.onClose();
  };

  const onDeleteHandler = () => {
    props.onDelete(omniDMO);
  };
  ///////////////////////////////////////////

  return (
    <Dialog
      open={open}
      onClose={onCloseHandler}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>{" "}
      <DialogTabs
        tabIndex={tabIndex}
        setTabIndex={setTabIndex}
        variant="fullWidth"
        scrollButtons
        allowScrollButtonsMobile
      >
        <Tab
          key={`tab0`}
          icon={<InfoRoundedIcon />}
          label={practiceNameValid ? "Information" : "Information *"}
        />
        {/* <Tab key={`tab2`} icon={<ScheduleRoundedIcon />} label="Business Hours" /> */}
      </DialogTabs>
      <DialogContent>
        {/* INFORMATION TAB */}
        <DialogTab selectedIndex={tabIndex} index={0}>
          {/* General Information  */}
          <Grid container>
            <Typography
              variant="overline"
              sx={{ marginTop: theme.spacing(1), marginBottom: `-${theme.spacing(1)}` }}
            >
              General Information
            </Typography>

            <Grid container item spacing={1}>
              <Grid item xs={12} sm={8}>
                <OmniTextField
                  label="Name"
                  type="textInput"
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"name"}
                  required
                  value={editedDMO.omniData.name}
                  error={!practiceNameValid}
                  onChange={event => {
                    udpatePracticeDMO(event);
                  }}
                  placeholder={"The name for this new practice"}
                  autoFocus
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <OmniTextField
                  label="Practice Type"
                  type="textInput"
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"practiceType"}
                  value={editedDMO.omniData.practiceType}
                  onChange={event => {
                    udpatePracticeDMO(event);
                  }}
                  placeholder={"Type"}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <OmniPhoneNumberField
                  label="Phone Number"
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"phone"}
                  value={editedDMO.omniData.phone}
                  error={!practicePhoneValid}
                  onChange={event => {
                    udpatePracticeDMO(event);
                  }}
                  onFocus={event => event.target.select()}
                  placeholder={"The phone number for this new practice"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <OmniTextField
                  label="Email"
                  required
                  type="textInput"
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"email"}
                  value={editedDMO.omniData.email}
                  error={!practiceEmailValid}
                  onChange={event => {
                    udpatePracticeDMO(event);
                  }}
                  placeholder={"Primary Email Address"}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Location Information */}
          <Grid container>
            <Typography
              variant="overline"
              sx={{ marginTop: theme.spacing(1), marginBottom: `-${theme.spacing(1)}` }}
            >
              Location Information
            </Typography>
            <Grid container item spacing={1}>
              <Grid item xs={8} sm={10}>
                <OmniTextField
                  label="Street Address"
                  type="textInput"
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"streetAddress"}
                  value={editedAddress.streetAddress}
                  onChange={event => {
                    updateAddressDMO(event);
                  }}
                  placeholder={"The street address for this new practice"}
                />
              </Grid>
              <Grid item xs={4} sm={2}>
                <OmniTextField
                  label="Country"
                  type="textInput"
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"country"}
                  value={editedAddress.country}
                  onChange={event => {
                    updateAddressDMO(event);
                  }}
                  placeholder={"USA"}
                />
              </Grid>

              <Grid item xs={12} md={7}>
                <OmniTextField
                  label="Locality"
                  type="textInput"
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"locality"}
                  value={editedAddress.locality}
                  onChange={event => {
                    updateAddressDMO(event);
                  }}
                  placeholder={"City or Locale of the practice"}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <OmniTextField
                  label="Region"
                  type="textInput"
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"region"}
                  value={editedAddress.region}
                  onChange={event => {
                    updateAddressDMO(event);
                  }}
                  placeholder={"State"}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <OmniTextField
                  label="Postal Code"
                  type="textInput"
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"postalCode"}
                  value={editedAddress.postalCode}
                  onChange={event => {
                    updateAddressDMO(event);
                  }}
                  placeholder={"Enter the postal, or zip, code for this practice"}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Practice Manager Information */}
          {/* <Grid container>
            <Typography
              variant="overline"
              sx={{ marginTop: theme.spacing(1), marginBottom: `-${theme.spacing(1)}` }}
            >
              Practice Manager Information{" "}
            </Typography>
            <Grid container item spacing={1}>
              <Grid item xs={12}>
                <OmniTextField
                  label="Manager Name"
                  type="textInput"
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"practiceManagerName"}
                  value={editedDMO.omniData.practiceManagerName}
                  onChange={event => {
                    udpatePracticeDMO(event);
                  }}
                  placeholder={"Enter the practice manager's first and last name"}
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <OmniPhoneNumberField
                  label="Manager Phone Number"
                  type="textInput"
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"practiceManagerPhone"}
                  value={editedDMO.omniData.practiceManagerPhone}
                  // error={!practiceManagerPhoneValid}
                  onChange={event => {
                    udpatePracticeDMO(event);
                  }}
                  onFocus={event => event.target.select()}
                  placeholder={"Enter the practice manager's phone number"}
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <OmniTextField
                  label="Manager Email"
                  type="textInput"
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"practiceManagerEmail"}
                  value={editedDMO.omniData.practiceManagerEmail}
                  // error={!practiceManagerEmailValid}
                  onChange={event => {
                    udpatePracticeDMO(event);
                  }}
                  placeholder={"Enter the practice manager's email address"}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid> */}
        </DialogTab>

        {/* Business Hours */}
        {/* <DialogTab selectedIndex={tabIndex} index={1}>
          <Grid container>
            <Typography
              variant="overline"
              sx={{ marginTop: theme.spacing(1), marginBottom: `-${theme.spacing(1)}` }}
            >
              Business Hours
            </Typography>
            <Grid container item spacing={1}>
              <Grid
                container
                item
                direction="row"
                justifyContent="space-around"
                alignItems="center"
                spacing={1}
              >
                <Grid item xs={4}>
                  <FormControlLabel
                    sx={{ ml: theme.spacing(0.25), mt: "2px" }}
                    control={
                      <Checkbox
                        color="secondary"
                        id={"monday"}
                        checked={editedBusinessHours.monday}
                        onChange={event => {
                          updateBusinessHoursDMO(event);
                        }}
                      />
                    }
                    label="Monday"
                    labelPlacement="end"
                  />
                </Grid>
                <Grid item xs={4}>
                  <OmniTimePicker
                    name={uuid()} //prevents AutoComplete Suggestions
                    id={"mondayOpen"}
                    value={editedBusinessHours.mondayOpen}
                    disabled={!editedBusinessHours.monday}
                    renderInput={props => <OmniTextField {...props} />}
                    onChange={event => {
                      // Have to add information to the event to suppor update mechanism
                      // From material-ui-pickers docs:
                      // KeyBoardDatePicker onChange * (date: DateIOType, value?: string | null) => void
                      let dateValue = null;
                      if (event && event.toString() !== "Invalid Date") {
                        dateValue = event.toISOString();
                      }
                      let dateInformation = {
                        target: { id: "mondayOpen", value: `${dateValue}` },
                      };
                      updateBusinessHoursDMO(dateInformation);
                    }}
                    label="Office Opens"
                    inputVariant="outlined"
                    KeyboardButtonProps={{
                      "aria-label": "change time",
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <OmniTimePicker
                    name={uuid()} //prevents AutoComplete Suggestions
                    id={"mondayClose"}
                    value={editedBusinessHours.mondayClose}
                    disabled={!editedBusinessHours.monday}
                    renderInput={props => <OmniTextField {...props} />}
                    onChange={event => {
                      // Have to add information to the event to suppor update mechanism
                      // From material-ui-pickers docs:
                      // KeyBoardDatePicker onChange * (date: DateIOType, value?: string | null) => void
                      let dateValue = "";
                      if (event && event.toString() !== "Invalid Date") {
                        dateValue = event.toISOString();
                      }
                      let dateInformation = {
                        target: { id: "mondayClose", value: `${dateValue}` },
                      };
                      updateBusinessHoursDMO(dateInformation);
                    }}
                    label="Office Closes"
                    inputVariant="outlined"
                    KeyboardButtonProps={{
                      "aria-label": "change time",
                    }}
                    helperText={""}
                  />
                </Grid>
              </Grid>

              <Grid
                container
                item
                direction="row"
                justifyContent="space-around"
                alignItems="center"
                spacing={1}
              >
                <Grid item xs={4}>
                  <FormControlLabel
                    sx={{ ml: theme.spacing(0.25), mt: "2px" }}
                    control={
                      <Checkbox
                        color="secondary"
                        id={"tuesday"}
                        checked={editedBusinessHours.tuesday}
                        onChange={event => {
                          updateBusinessHoursDMO(event);
                        }}
                      />
                    }
                    label="Tuesday"
                    labelPlacement="end"
                  />
                </Grid>
                <Grid item xs={4}>
                  <OmniTimePicker
                    name={uuid()} //prevents AutoComplete Suggestions
                    id={"tuesdayOpen"}
                    value={editedBusinessHours.tuesdayOpen}
                    disabled={!editedBusinessHours.tuesday}
                    renderInput={props => <OmniTextField {...props} />}
                    onChange={event => {
                      // Have to add information to the event to suppor update mechanism
                      // From material-ui-pickers docs:
                      // KeyBoardDatePicker onChange * (date: DateIOType, value?: string | null) => void
                      let dateValue = null;
                      if (event && event.toString() !== "Invalid Date") {
                        dateValue = event.toISOString();
                      }
                      let dateInformation = {
                        target: { id: "tuesdayOpen", value: `${dateValue}` },
                      };
                      updateBusinessHoursDMO(dateInformation);
                    }}
                    label="Office Opens"
                    KeyboardButtonProps={{
                      "aria-label": "change time",
                    }}
                    helperText={""}
                  />
                </Grid>
                <Grid item xs={4}>
                  <OmniTimePicker
                    name={uuid()} //prevents AutoComplete Suggestions
                    id={"tuesdayClose"}
                    value={editedBusinessHours.tuesdayClose}
                    disabled={!editedBusinessHours.tuesday}
                    renderInput={props => <OmniTextField {...props} />}
                    onChange={event => {
                      // Have to add information to the event to suppor update mechanism
                      // From material-ui-pickers docs:
                      // KeyBoardDatePicker onChange * (date: DateIOType, value?: string | null) => void
                      let dateValue = "";
                      if (event && event.toString() !== "Invalid Date") {
                        dateValue = event.toISOString();
                      }
                      let dateInformation = {
                        target: { id: "tuesdayClose", value: `${dateValue}` },
                      };
                      updateBusinessHoursDMO(dateInformation);
                    }}
                    label="Office Closes"
                    inputVariant="outlined"
                    mask="__:__ _M"
                    KeyboardButtonProps={{
                      "aria-label": "change time",
                    }}
                    helperText={""}
                  />
                </Grid>
              </Grid>

              <Grid
                container
                item
                direction="row"
                justifyContent="space-evenly"
                alignItems="center"
                spacing={1}
              >
                <Grid item xs={4}>
                  <FormControlLabel
                    sx={{ ml: theme.spacing(0.25), mt: "2px" }}
                    control={
                      <Checkbox
                        color="secondary"
                        id={"wednesday"}
                        checked={editedBusinessHours.wednesday}
                        onChange={event => {
                          updateBusinessHoursDMO(event);
                        }}
                      />
                    }
                    label="Wednesday"
                    labelPlacement="end"
                  />
                </Grid>
                <Grid item xs={4}>
                  <OmniTimePicker
                    name={uuid()} //prevents AutoComplete Suggestions
                    id={"wednesdayOpen"}
                    value={editedBusinessHours.wednesdayOpen}
                    disabled={!editedBusinessHours.wednesday}
                    renderInput={props => <OmniTextField {...props} />}
                    onChange={event => {
                      // Have to add information to the event to suppor update mechanism
                      // From material-ui-pickers docs:
                      // KeyBoardDatePicker onChange * (date: DateIOType, value?: string | null) => void
                      let dateValue = "";
                      if (event && event.toString() !== "Invalid Date") {
                        dateValue = event.toISOString();
                      }
                      let dateInformation = {
                        target: { id: "wednesdayOpen", value: `${dateValue}` },
                      };
                      updateBusinessHoursDMO(dateInformation);
                    }}
                    label="Office Opens"
                    mask="__:__ _M"
                    KeyboardButtonProps={{
                      "aria-label": "change time",
                    }}
                    helperText={""}
                  />
                </Grid>
                <Grid item xs={4}>
                  <OmniTimePicker
                    name={uuid()} //prevents AutoComplete Suggestions
                    id={"wednesdayClose"}
                    value={editedBusinessHours.wednesdayClose}
                    disabled={!editedBusinessHours.wednesday}
                    renderInput={props => <OmniTextField {...props} />}
                    onChange={event => {
                      // Have to add information to the event to suppor update mechanism
                      // From material-ui-pickers docs:
                      // KeyBoardDatePicker onChange * (date: DateIOType, value?: string | null) => void
                      let dateValue = "";
                      if (event && event.toString() !== "Invalid Date") {
                        dateValue = event.toISOString();
                      }
                      let dateInformation = {
                        target: { id: "wednesdayClose", value: `${dateValue}` },
                      };
                      updateBusinessHoursDMO(dateInformation);
                    }}
                    label="Office Closes"
                    inputVariant="outlined"
                    mask="__:__ _M"
                    KeyboardButtonProps={{
                      "aria-label": "change time",
                    }}
                    helperText={""}
                  />
                </Grid>
              </Grid>

              <Grid
                container
                item
                direction="row"
                justifyContent="space-evenly"
                alignItems="center"
                spacing={1}
              >
                <Grid item xs={4}>
                  <FormControlLabel
                    sx={{ ml: theme.spacing(0.25), mt: "2px" }}
                    control={
                      <Checkbox
                        color="secondary"
                        id={"thursday"}
                        checked={editedBusinessHours.thursday}
                        onChange={event => {
                          updateBusinessHoursDMO(event);
                        }}
                      />
                    }
                    label="Thursday"
                    labelPlacement="end"
                  />
                </Grid>
                <Grid item xs={4}>
                  <OmniTimePicker
                    name={uuid()} //prevents AutoComplete Suggestions
                    id={"thursdayOpen"}
                    value={editedBusinessHours.thursdayOpen}
                    disabled={!editedBusinessHours.thursday}
                    renderInput={props => <OmniTextField {...props} />}
                    onChange={event => {
                      // Have to add information to the event to suppor update mechanism
                      // From material-ui-pickers docs:
                      // KeyBoardDatePicker onChange * (date: DateIOType, value?: string | null) => void
                      let dateValue = "";
                      if (event && event.toString() !== "Invalid Date") {
                        dateValue = event.toISOString();
                      }
                      let dateInformation = {
                        target: { id: "thursdayOpen", value: `${dateValue}` },
                      };
                      updateBusinessHoursDMO(dateInformation);
                    }}
                    label="Office Opens"
                    mask="__:__ _M"
                    KeyboardButtonProps={{
                      "aria-label": "change time",
                    }}
                    helperText={""}
                  />
                </Grid>
                <Grid item xs={4}>
                  <OmniTimePicker
                    name={uuid()} //prevents AutoComplete Suggestions
                    id={"thursdayClose"}
                    value={editedBusinessHours.thursdayClose}
                    disabled={!editedBusinessHours.thursday}
                    renderInput={props => <OmniTextField {...props} />}
                    onChange={event => {
                      // Have to add information to the event to suppor update mechanism
                      // From material-ui-pickers docs:
                      // KeyBoardDatePicker onChange * (date: DateIOType, value?: string | null) => void
                      let dateValue = "";
                      if (event && event.toString() !== "Invalid Date") {
                        dateValue = event.toISOString();
                      }
                      let dateInformation = {
                        target: { id: "thursdayClose", value: `${dateValue}` },
                      };
                      updateBusinessHoursDMO(dateInformation);
                    }}
                    label="Office Closes"
                    inputVariant="outlined"
                    mask="__:__ _M"
                    KeyboardButtonProps={{
                      "aria-label": "change time",
                    }}
                    helperText={""}
                  />
                </Grid>
              </Grid>

              <Grid
                container
                item
                direction="row"
                justifyContent="space-evenly"
                alignItems="center"
                spacing={1}
              >
                <Grid item xs={4}>
                  <FormControlLabel
                    sx={{ ml: theme.spacing(0.25), mt: "2px" }}
                    control={
                      <Checkbox
                        color="secondary"
                        id={"friday"}
                        checked={editedBusinessHours.friday}
                        onChange={event => {
                          updateBusinessHoursDMO(event);
                        }}
                      />
                    }
                    label="Friday"
                    labelPlacement="end"
                  />
                </Grid>
                <Grid item xs={4}>
                  <OmniTimePicker
                    name={uuid()} //prevents AutoComplete Suggestions
                    id={"fridayOpen"}
                    value={editedBusinessHours.fridayOpen}
                    disabled={!editedBusinessHours.friday}
                    renderInput={props => <OmniTextField {...props} />}
                    onChange={event => {
                      // Have to add information to the event to suppor update mechanism
                      // From material-ui-pickers docs:
                      // KeyBoardDatePicker onChange * (date: DateIOType, value?: string | null) => void
                      let dateValue = "";
                      if (event && event.toString() !== "Invalid Date") {
                        dateValue = event.toISOString();
                      }
                      let dateInformation = {
                        target: { id: "fridayOpen", value: `${dateValue}` },
                      };
                      updateBusinessHoursDMO(dateInformation);
                    }}
                    label="Office Opens"
                    mask="__:__ _M"
                    KeyboardButtonProps={{
                      "aria-label": "change time",
                    }}
                    helperText={""}
                  />
                </Grid>
                <Grid item xs={4}>
                  <OmniTimePicker
                    name={uuid()} //prevents AutoComplete Suggestions
                    id={"fridayClose"}
                    value={editedBusinessHours.fridayClose}
                    disabled={!editedBusinessHours.friday}
                    renderInput={props => <OmniTextField {...props} />}
                    onChange={event => {
                      // Have to add information to the event to suppor update mechanism
                      // From material-ui-pickers docs:
                      // KeyBoardDatePicker onChange * (date: DateIOType, value?: string | null) => void
                      let dateValue = "";
                      if (event && event.toString() !== "Invalid Date") {
                        dateValue = event.toISOString();
                      }
                      let dateInformation = {
                        target: { id: "fridayClose", value: `${dateValue}` },
                      };
                      updateBusinessHoursDMO(dateInformation);
                    }}
                    label="Office Closes"
                    inputVariant="outlined"
                    mask="__:__ _M"
                    KeyboardButtonProps={{
                      "aria-label": "change time",
                    }}
                    helperText={""}
                  />
                </Grid>
              </Grid>

              <Grid
                container
                item
                direction="row"
                justifyContent="space-evenly"
                alignItems="center"
                spacing={1}
              >
                <Grid item xs={4}>
                  <FormControlLabel
                    sx={{ ml: theme.spacing(0.25), mt: "2px" }}
                    control={
                      <Checkbox
                        color="secondary"
                        id={"saturday"}
                        checked={editedBusinessHours.saturday}
                        onChange={event => {
                          updateBusinessHoursDMO(event);
                        }}
                      />
                    }
                    label="Saturday"
                    labelPlacement="end"
                  />
                </Grid>
                <Grid item xs={4}>
                  <OmniTimePicker
                    name={uuid()} //prevents AutoComplete Suggestions
                    id={"saturdayOpen"}
                    value={editedBusinessHours.saturdayOpen}
                    disabled={!editedBusinessHours.saturday}
                    renderInput={props => <OmniTextField {...props} />}
                    onChange={event => {
                      // Have to add information to the event to suppor update mechanism
                      // From material-ui-pickers docs:
                      // KeyBoardDatePicker onChange * (date: DateIOType, value?: string | null) => void
                      let dateValue = "";
                      if (event && event.toString() !== "Invalid Date") {
                        dateValue = event.toISOString();
                      }
                      let dateInformation = {
                        target: { id: "saturdayOpen", value: `${dateValue}` },
                      };
                      updateBusinessHoursDMO(dateInformation);
                    }}
                    label="Office Opens"
                    mask="__:__ _M"
                    KeyboardButtonProps={{
                      "aria-label": "change time",
                    }}
                    helperText={""}
                  />
                </Grid>
                <Grid item xs={4}>
                  <OmniTimePicker
                    name={uuid()} //prevents AutoComplete Suggestions
                    id={"saturdayClose"}
                    value={editedBusinessHours.saturdayClose}
                    disabled={!editedBusinessHours.saturday}
                    renderInput={props => <OmniTextField {...props} />}
                    onChange={event => {
                      // Have to add information to the event to suppor update mechanism
                      // From material-ui-pickers docs:
                      // KeyBoardDatePicker onChange * (date: DateIOType, value?: string | null) => void
                      let dateValue = "";
                      if (event && event.toString() !== "Invalid Date") {
                        dateValue = event.toISOString();
                      }
                      let dateInformation = {
                        target: { id: "saturdayClose", value: `${dateValue}` },
                      };
                      updateBusinessHoursDMO(dateInformation);
                    }}
                    label="Office Closes"
                    inputVariant="outlined"
                    mask="__:__ _M"
                    KeyboardButtonProps={{
                      "aria-label": "change time",
                    }}
                    helperText={""}
                  />
                </Grid>
              </Grid>

              <Grid
                container
                item
                direction="row"
                justifyContent="space-evenly"
                alignItems="center"
                spacing={1}
              >
                <Grid item xs={4}>
                  <FormControlLabel
                    sx={{ ml: theme.spacing(0.25), mt: "2px" }}
                    control={
                      <Checkbox
                        color="secondary"
                        id={"sunday"}
                        checked={editedBusinessHours.sunday}
                        onChange={event => {
                          updateBusinessHoursDMO(event);
                        }}
                      />
                    }
                    label="Sunday"
                    labelPlacement="end"
                  />
                </Grid>
                <Grid item xs={4}>
                  <OmniTimePicker
                    name={uuid()} //prevents AutoComplete Suggestions
                    id={"sundayOpen"}
                    value={editedBusinessHours.sundayOpen}
                    disabled={!editedBusinessHours.sunday}
                    renderInput={props => <OmniTextField {...props} />}
                    onChange={event => {
                      // Have to add information to the event to suppor update mechanism
                      // From material-ui-pickers docs:
                      // KeyBoardDatePicker onChange * (date: DateIOType, value?: string | null) => void
                      let dateValue = "";
                      if (event && event.toString() !== "Invalid Date") {
                        dateValue = event.toISOString();
                      }
                      let dateInformation = {
                        target: { id: "sundayOpen", value: `${dateValue}` },
                      };
                      updateBusinessHoursDMO(dateInformation);
                    }}
                    label="Office Opens"
                    mask="__:__ _M"
                    KeyboardButtonProps={{
                      "aria-label": "change time",
                    }}
                    helperText={""}
                  />
                </Grid>
                <Grid item xs={4}>
                  <OmniTimePicker
                    name={uuid()} //prevents AutoComplete Suggestions
                    id={"sundayClose"}
                    value={editedBusinessHours.sundayClose}
                    disabled={!editedBusinessHours.sunday}
                    renderInput={props => <OmniTextField {...props} />}
                    onChange={event => {
                      // Have to add information to the event to suppor update mechanism
                      // From material-ui-pickers docs:
                      // KeyBoardDatePicker onChange * (date: DateIOType, value?: string | null) => void
                      let dateValue = "";
                      if (event && event.toString() !== "Invalid Date") {
                        dateValue = event.toISOString();
                      }
                      let dateInformation = {
                        target: { id: "sundayClose", value: `${dateValue}` },
                      };
                      updateBusinessHoursDMO(dateInformation);
                    }}
                    label="Office Closes"
                    inputVariant="outlined"
                    mask="__:__ _M"
                    KeyboardButtonProps={{
                      "aria-label": "change time",
                    }}
                    helperText={""}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogTab> */}
      </DialogContent>
      <DialogActions>
        <Grid container justifyContent="space-between">
          <Grid item xs={6}>
            {editedDMO._id != null && (
              <OmniResponsiveButton
                icon={<DeleteIcon />}
                onClick={() => setShowConfirm(true)}
                buttonText={deleteMessage}
                tooltipTitle=""
              />
            )}
          </Grid>
          <Grid container item justifyContent="flex-end" xs={6} spacing={1}>
            <Grid item>
              {cancelMessage && (
                <Button onClick={onCloseHandler} color="secondary" variant="contained">
                  {cancelMessage}
                </Button>
              )}
            </Grid>
            <Grid item>
              <Button
                onClick={onAcceptHandler}
                color="secondary"
                disabled={!readyOk}
                variant="contained"
              >
                {okMessage}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </DialogActions>
      {showConfirm && (
        <AlertDialog
          open={showConfirm}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want to remove this practice?"}
          onAccept={() => {
            onDeleteHandler();
            setShowConfirm(false);
          }}
          onClose={() => {
            setShowConfirm(false);
          }}
        />
      )}
    </Dialog>
  );
};

const mapStateToProps = state => ({});

const mapDispatchToProps = {};
export default connect(mapStateToProps, mapDispatchToProps)(PracticeDialog);
